import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import Img from 'gatsby-image';
import { graphql, useStaticQuery } from 'gatsby';

import { GatsbyImage } from '@utilities/gatsby-image';

// ? Start of re-usable component
// interface Props {
//   totalSlides: number;
//   images: [];
//   imagesPerSlide: number;
// }

export const SanmarLogoCycler = () => {
  const [firstSlide, setFirstSlide] = useState(2);
  const [secondSlide, setSecondSlide] = useState(1);
  const [thirdSlide, setThirdSlide] = useState(0);
  const largestLogosContainerRef = useRef<HTMLDivElement>(null);
  const [logosContainerHeight, setLogosContainerHeight] = useState(0);

  // ? Start of re-usable component
  // const [state, setState] = useState([]);

  // useEffect(() => {
  //   let newState = [];
  //   for (let i = totalSlides; i > 0; --i) {
  //     newState.push({
  //       order: i,
  //       images: [

  //       ]
  //     })
  //   }
  // }, [])

  const transitions = {
    first: {
      zIndex: 3,
      scale: 1, // zoom forward effect
      opacity: 1,
      y: 0, // zoom forward effect
      transition: {
        duration: 1,
      },
    },
    second: {
      zIndex: 2,
      y: -25, // zoom forward effect
      scale: 0.85, // zoom forward effect
      opacity: 0,
      transition: {
        duration: 1,
      },
    },
    third: {
      zIndex: 1,
      y: -25, // zoom forward effect
      scale: 0.85, // zoom forward effect
      opacity: 0,
      transition: {
        duration: 1,
      },
    },
    exit: {
      zIndex: 4,
      opacity: 0,
      scale: 0.5, // zoom back effect
      transition: {
        duration: 0.3,
      },
    },
    enter: {
      zIndex: 0,
      opacity: 0,
      transition: {
        duration: 0.3,
      },
    },
  };

  const setSlidePosition = (slide: number) => {
    switch (slide) {
      case 0:
        return 'third';
      case 1:
        return 'second';
      case 2:
        return 'first';
      case 3:
        return 'exit';
      default:
        return 'enter';
    }
  };

  useEffect(() => {
    const incrementSlidePosition = setInterval(() => {
      setFirstSlide(firstSlide + 1);
      setSecondSlide(secondSlide + 1);
      setThirdSlide(thirdSlide + 1);
    }, 4000);
    return () => clearInterval(incrementSlidePosition);
  }, [firstSlide, secondSlide, thirdSlide]);

  // Timing the move from exit to enter stages in the animation
  useEffect(() => {
    if (firstSlide === 3) {
      setTimeout(() => {
        setFirstSlide(4);
        setTimeout(() => {
          setFirstSlide(0);
        }, 250);
      }, 150);
    }
    if (secondSlide === 3) {
      setTimeout(() => {
        setSecondSlide(4);
        setTimeout(() => {
          setSecondSlide(0);
        }, 250);
      }, 150);
    }
    if (thirdSlide === 3) {
      setTimeout(() => {
        setThirdSlide(4);
        setTimeout(() => {
          setThirdSlide(0);
        }, 250);
      }, 150);
    }
  }, [firstSlide, secondSlide, thirdSlide]);

  useEffect(() => {
    if (largestLogosContainerRef.current) {
      setLogosContainerHeight(largestLogosContainerRef.current.offsetHeight);
    }
  }, [largestLogosContainerRef]);

  const data = useStaticQuery(graphql`
    {
      carhartt: file(relativePath: { eq: "catalogs/carhartt-logo.png" }) {
        id
        childImageSharp {
          fixed {
            ...GatsbyImageSharpFixed
          }
        }
      }
      nike: file(relativePath: { eq: "catalogs/nike-logo.png" }) {
        id
        childImageSharp {
          fixed {
            ...GatsbyImageSharpFixed
          }
        }
      }
      sport: file(relativePath: { eq: "catalogs/sport-tek-logo.png" }) {
        id
        childImageSharp {
          fixed {
            ...GatsbyImageSharpFixed
          }
        }
      }
      new: file(relativePath: { eq: "catalogs/new-era-logo.png" }) {
        id
        childImageSharp {
          fixed {
            ...GatsbyImageSharpFixed
          }
        }
      }
      red: file(relativePath: { eq: "catalogs/red-kap-logo.png" }) {
        id
        childImageSharp {
          fixed {
            ...GatsbyImageSharpFixed
          }
        }
      }
      ogio: file(relativePath: { eq: "catalogs/ogio-logo.png" }) {
        id
        childImageSharp {
          fixed {
            ...GatsbyImageSharpFixed
          }
        }
      }
      portCompany: file(
        relativePath: { eq: "catalogs/port-and-company-logo.png" }
      ) {
        id
        childImageSharp {
          fixed {
            ...GatsbyImageSharpFixed
          }
        }
      }
      cornerstone: file(relativePath: { eq: "catalogs/cornerstone-logo.png" }) {
        id
        childImageSharp {
          fixed {
            ...GatsbyImageSharpFixed
          }
        }
      }
      eddie: file(relativePath: { eq: "catalogs/eddie-bauer-logo.png" }) {
        id
        childImageSharp {
          fixed {
            ...GatsbyImageSharpFixed
          }
        }
      }
      portAuthority: file(
        relativePath: { eq: "catalogs/port-authority-logo.png" }
      ) {
        id
        childImageSharp {
          fixed {
            ...GatsbyImageSharpFixed
          }
        }
      }
      fruit: file(relativePath: { eq: "catalogs/fruit-of-the-loom-logo.png" }) {
        id
        childImageSharp {
          fixed {
            ...GatsbyImageSharpFixed
          }
        }
      }
      hanes: file(relativePath: { eq: "catalogs/hanes-logo.png" }) {
        id
        childImageSharp {
          fixed {
            ...GatsbyImageSharpFixed
          }
        }
      }
    }
  `);

  return (
    <Container logosContainerHeight={logosContainerHeight}>
      <SizeContainer>
        <Logos
          ref={largestLogosContainerRef}
          initial="first"
          variants={transitions}
          animate={setSlidePosition(firstSlide)}
        >
          <Logo fixed={data.carhartt.childImageSharp.fixed} />
          <Logo fixed={data.nike.childImageSharp.fixed} />
          <Logo fixed={data.sport.childImageSharp.fixed} />
          <Logo fixed={data.new.childImageSharp.fixed} />
        </Logos>
        <Logos
          initial="second"
          variants={transitions}
          animate={setSlidePosition(secondSlide)}
        >
          <Logo fixed={data.red.childImageSharp.fixed} />
          <Logo fixed={data.ogio.childImageSharp.fixed} />
          <Logo fixed={data.portCompany.childImageSharp.fixed} />
          <Logo fixed={data.cornerstone.childImageSharp.fixed} />
        </Logos>
        <Logos
          initial="third"
          variants={transitions}
          animate={setSlidePosition(thirdSlide)}
        >
          <Logo fixed={data.eddie.childImageSharp.fixed} />
          <Logo fixed={data.portAuthority.childImageSharp.fixed} />
          <Logo fixed={data.fruit.childImageSharp.fixed} />
          <Logo fixed={data.hanes.childImageSharp.fixed} />
        </Logos>
      </SizeContainer>
    </Container>
  );
};

interface ContainerProps {
  logosContainerHeight: number;
}

const Container = styled.div<ContainerProps>`
  display: grid;
  grid-template-columns: 1fr;
  justify-items: center;
  height: ${({ logosContainerHeight }) =>
    logosContainerHeight && logosContainerHeight}px;
`;

const SizeContainer = styled.div`
  position: relative;
  width: 100%;
`;

const Logos = styled(motion.div)`
  position: absolute;
  left: 0;
  top: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-around;
  width: 100%;
`;

const Logo = styled(Img)<GatsbyImage>`
  margin: 25px 0;
`;
